import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { motion, useScroll, useSpring } from "framer-motion";

import Logo from "@/assets/logo.svg";
import SymbolLogo from "@/assets/symbol-logo.svg";
import { GlassButton } from "@/components/glassButton";
import { useTracking } from "@/hooks/useTracking";
import "./styles.scss";

interface SiteHeaderProps {
  variant?: "isp" | "mvno";
}

const NAV = [
  { label: "ISP", to: "/", match: "/" },
  { label: "MVNO", to: "/mvno", match: "/mvno", badge: "Novo" },
  {
    label: "Contato",
    to: "https://linktr.ee/provenduz",
    match: "__none__",
    external: true,
  },
];

export const SiteHeader = ({ variant = "isp" }: SiteHeaderProps) => {
  const { sendEvent } = useTracking();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 30,
    mass: 0.3,
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const track = (label: string) =>
    sendEvent("button_clicked", {
      event_category: "header",
      event_label: label,
      event_action: "nav_click",
      value: 1,
    });

  const portalUrl = import.meta.env.URL_PORTAL as string | undefined;

  return (
    <header className={`site-header site-header--${variant}`}>
      <motion.span
        className="site-header__progress"
        style={{ scaleX: progress }}
      />
      <div
        className={`site-header__bar ${scrolled ? "site-header__bar--scrolled" : ""}`}
      >
        <Link to="/" className="site-header__brand" onClick={() => track("logo")}>
          <img src={Logo} alt="Provenduz" className="site-header__logo" />
          <img
            src={SymbolLogo}
            alt="Provenduz"
            className="site-header__logo--symbol"
          />
        </Link>

        <nav className="site-header__nav">
          {NAV.map((item) =>
            item.external ? (
              <a
                key={item.label}
                href={item.to}
                target="_blank"
                rel="noreferrer"
                onClick={() => track(item.label)}
              >
                {item.label}
              </a>
            ) : (
              <Link
                key={item.label}
                to={item.to}
                className={pathname === item.match ? "is-active" : ""}
                onClick={() => track(item.label)}
              >
                {item.label}
                {item.badge && (
                  <span className="site-header__badge">{item.badge}</span>
                )}
              </Link>
            )
          )}
        </nav>

        <div className="site-header__actions">
          {portalUrl ? (
            <GlassButton
              variant={variant}
              href={portalUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => track("login")}
              className="site-header__cta"
            >
              Entrar
            </GlassButton>
          ) : (
            <GlassButton
              variant={variant}
              href={variant === "mvno" ? "/mvno#ativacao" : "/#simulador"}
              onClick={() => track("cta")}
              className="site-header__cta"
            >
              {variant === "mvno" ? "Quero ser operadora" : "Montar meu app"}
            </GlassButton>
          )}

          <button
            className="site-header__burger"
            aria-label="Abrir menu"
            onClick={() => setOpen((v) => !v)}
          >
            <i className={`bx ${open ? "bx-x" : "bx-menu"}`} />
          </button>
        </div>
      </div>

      {open && (
        <div className="site-header__drawer">
          {NAV.map((item) =>
            item.external ? (
              <a
                key={item.label}
                href={item.to}
                target="_blank"
                rel="noreferrer"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </a>
            ) : (
              <Link key={item.label} to={item.to} onClick={() => setOpen(false)}>
                {item.label}
                {item.badge && (
                  <span className="site-header__badge">{item.badge}</span>
                )}
              </Link>
            )
          )}
        </div>
      )}
    </header>
  );
};
